import React, { useContext } from "react";
import { Col, Row, Spinner } from "reactstrap";
import HeaderLogo from "src/components/HeaderLogo";
import { ErrorType, RequestStatus } from "src/lib/api/types";
import { useApi } from "src/lib/ApiProvider";

import { useNavigate } from "react-router-dom";
import { Footer } from "src/components/Footer";
import { isErrorResponse } from "src/lib/api/utils";
import { ReceiptsContext } from "src/lib/receiptsContext";
import ErrorMessage from "./ErrorMessage";
import { ReceiptsForm } from "./ReceiptsForm";
import { ReceiptFormValues } from "./types";

export const RequestReceiptsForm = () => {
  const { receiptsStatus, searchReceipts } =
    useContext(ReceiptsContext);
  const [emailStatus, setEmailStatus] = React.useState<RequestStatus>("idle");
  const [errorType, setErrorType] = React.useState<ErrorType | null>(null);
  const [, setSearchParams] = React.useState<Record<string, string>>({});

  const { api, authApi } = useApi();
  const navigate = useNavigate();

  const handleRegisterEmail = React.useCallback(
    async (sessionToken: string, email: string) => {
      authApi
        .patch(
          `api/customer/v2/customers/me`,
          {
            email,
          },
          { headers: { authorization: `Bearer ${sessionToken}` } } // token has to be explicitely passed as it can't be taken from urlParams
        )
        .then((res) => {
          if (res.status === 200) {
            setEmailStatus("success");
          }
        })
        .catch((err) => {
          setEmailStatus("error");
          console.error(err);
        });
    },
    [authApi]
  );
  const handleSubmit = React.useCallback(
    async (values: ReceiptFormValues) => {
      setErrorType(null);
      try {
        const params = values as unknown as Record<string, string>;
        setSearchParams(params);

        const receiptsData = await searchReceipts(api, params);

        // Email registration is handled after successful receipt search
        if (values.email) {
          // Note: If registration will fail, this won't be reflected in the interface as user
          // will be redirected to receipts list. While working on related issue I decided not to
          // refactor this part as I believe it's better to show the receipts than to stop whole
          // customer from retrieving the receipt. See OASIS-18820 for improvement ticket.
          if (receiptsData && receiptsData.length > 0) {
            const sessionToken = receiptsData[0].sessionToken;
            const email = values.email!;
            handleRegisterEmail(sessionToken, email);
          } else {
            setEmailStatus("error");
          }
        }

        navigate("/list", { replace: false, state: { searchParams: params } });
      } catch (err) {
        if (isErrorResponse(err)) {
          setErrorType(err.response.data.type);
        } else {
          setErrorType(ErrorType.UNRECOGNIZED_ERR);
        }
      }
    },
    [api, handleRegisterEmail, searchReceipts, navigate]
  );
  
  
  return (
    <div
      style={{
        padding: "16px",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <HeaderLogo />
      <Col>
        <ReceiptsForm onSubmit={handleSubmit} emailStatus={emailStatus} />
        {receiptsStatus === "loading" && (
          <Row className="justify-content-center">
            <Spinner />
          </Row>
        )}
      </Col>
      {receiptsStatus === "error" && errorType !== null && (
        <Col>
          <Row className="justify-content-center">
            <ErrorMessage errorType={errorType} />
          </Row>
        </Col>
      )}
      <Footer />
    </div>
  );
};
